import React from 'react';
import { Link } from 'react-router-dom';
import { BellRing, FolderLock, Cake, ShieldCheck, ArrowRight } from 'lucide-react';
import MemberLayout from '../../components/MemberLayout';
import { Logo } from '../../components/ui';
import { useGroup, useMember } from '../../context/AuthContext';

const BENEFITS = [
  { icon: BellRing, title: 'Never miss batch news', text: 'AGM updates, events and school projects delivered straight to your inbox.' },
  { icon: FolderLock, title: 'Members-only documents', text: 'Financial reports, meeting minutes and Grade 1 admission details.' },
  { icon: Cake, title: 'Birthday wishes', text: 'A greeting from your batch mates on your special day.' },
  { icon: ShieldCheck, title: 'Your data stays private', text: 'Used only for group communication — never shared or sold.' },
];

export default function Home() {
  const { group } = useGroup();
  const { member } = useMember();

  return (
    <MemberLayout>
      <section className="overflow-hidden rounded-2xl bg-navy-900 text-white shadow-lg">
        <div className="grid items-center gap-8 p-8 md:grid-cols-[1fr_auto] md:p-12">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-400">Disce aut Discede</p>
            <h1 className="mt-3 font-display text-4xl font-bold leading-tight md:text-5xl">
              {group?.name || 'Royal College 2008 Batch'}<br /><span className="text-gold-400">Members register</span>
            </h1>
            <p className="mt-4 max-w-xl text-navy-100">
              Register once and keep your email and phone number up to date, so the committee can reach every member of our batch quickly.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {member ? (
                <Link to="/profile" className="btn-gold px-6 py-3 text-base">Go to my profile <ArrowRight size={18} /></Link>
              ) : (
                <>
                  <Link to="/register" className="btn-gold px-6 py-3 text-base">Register now <ArrowRight size={18} /></Link>
                  <Link to="/login" className="btn border border-navy-500 px-6 py-3 text-base text-white hover:bg-navy-800">I already registered</Link>
                </>
              )}
            </div>
          </div>
          <div className="hidden justify-center md:flex">
            <div className="rounded-full bg-navy-900 p-6 ring-1 ring-gold-500/30"><Logo size={200} /></div>
          </div>
        </div>
        <div className="h-1.5 bg-gold-500" />
      </section>

      <section className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {BENEFITS.map(b => (
          <div key={b.title} className="card p-5">
            <span className="inline-flex rounded-lg bg-gold-100 p-2 text-gold-800"><b.icon size={20} /></span>
            <h3 className="mt-3 font-semibold text-navy-900">{b.title}</h3>
            <p className="mt-1 text-sm text-gray-600">{b.text}</p>
          </div>
        ))}
      </section>

      <section className="card mt-10 p-6 md:p-8">
        <h2 className="font-display text-2xl font-bold text-navy-900">How it works</h2>
        <ol className="mt-4 grid gap-4 md:grid-cols-4">
          {['Fill in your details, NIC and RCU membership card', 'Verify your email with the link or code we send', 'Access Group documents and start receiving news', 'Confirm your details once a year when reminded'].map((s, i) => (
            <li key={s} className="flex gap-3">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-navy-900 font-semibold text-gold-400">{i + 1}</span>
              <span className="text-sm text-gray-700">{s}</span>
            </li>
          ))}
        </ol>
      </section>
    </MemberLayout>
  );
}
